import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const SEOContainer = styled.div`
  max-width: 100%;
`;

const TestResults = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid #eee;
`;

const TestGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin: 1rem 0;
`;

const TestCard = styled.div`
  background: white;
  padding: 1rem;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);

  h4 {
    margin: 0 0 0.5rem 0;
    color: #666;
    font-size: 0.9rem;
  }

  p {
    margin: 0;
    font-size: 1rem;
    color: ${props => props.status === 'good' ? '#28a745' : props.status === 'warning' ? '#ffc107' : '#dc3545'};
  }
`;

const TestButton = styled.button`
  padding: 0.5rem 1rem;
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 1rem;
  margin-bottom: 1rem;

  &:hover {
    background: #357abd;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const IssueList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1rem 0;
`;

const IssueItem = styled.li`
  margin: 0.5rem 0;
  padding: 0.75rem;
  background: #fff;
  border-left: 4px solid ${props => props.severity === 'error' ? '#dc3545' : props.severity === 'warning' ? '#ffc107' : '#28a745'};
  border-radius: 4px;
  font-size: 0.9rem;
`;

const TestSection = styled.div`
  margin-bottom: 1.5rem;

  h3 {
    margin: 0 0 1rem 0;
    color: var(--primary-color);
    font-size: 1.1rem;
  }
`;

const SEOTest = () => {
  const [testResults, setTestResults] = useState({
    meta: {
      title: null,
      description: null,
      keywords: null,
      viewport: null,
      robots: null,
      canonical: null,
      ogTags: null,
      twitterTags: null
    },
    structure: {
      headings: null,
      imgAlt: null,
      links: null,
      sitemap: null,
      robotsTxt: null
    },
    performance: {
      loadTime: null,
      mobileResponsive: null,
      https: null
    }
  });

  const [issues, setIssues] = useState([]);

  const checkMetaTags = () => {
    const head = document.head;
    const results = {
      title: document.title || null,
      description: head.querySelector('meta[name="description"]')?.content,
      keywords: head.querySelector('meta[name="keywords"]')?.content,
      viewport: head.querySelector('meta[name="viewport"]')?.content,
      robots: head.querySelector('meta[name="robots"]')?.content,
      canonical: head.querySelector('link[rel="canonical"]')?.href,
      ogTags: head.querySelectorAll('meta[property^="og:"]').length,
      twitterTags: head.querySelectorAll('meta[name^="twitter:"]').length
    };

    setTestResults(prev => ({ ...prev, meta: results }));

    // Log issues
    const newIssues = [];
    if (!results.title) {
      newIssues.push({ severity: 'error', message: 'Page title is missing' });
    } else if (results.title.length < 10 || results.title.length > 60) {
      newIssues.push({ severity: 'warning', message: 'Page title length should be between 10-60 characters' });
    }

    if (!results.description) {
      newIssues.push({ severity: 'error', message: 'Meta description is missing' });
    } else if (results.description.length < 50 || results.description.length > 160) {
      newIssues.push({ severity: 'warning', message: 'Meta description length should be between 50-160 characters' });
    }

    if (!results.canonical) {
      newIssues.push({ severity: 'warning', message: 'Canonical URL is missing' });
    }

    if (results.ogTags === 0) {
      newIssues.push({ severity: 'warning', message: 'Open Graph tags are missing' });
    }

    return newIssues;
  };

  const checkStructure = () => {
    const results = {
      headings: {
        h1: document.querySelectorAll('h1').length,
        total: document.querySelectorAll('h1, h2, h3, h4, h5, h6').length
      },
      imgAlt: {
        total: document.querySelectorAll('img').length,
        withAlt: document.querySelectorAll('img[alt]').length
      },
      links: {
        total: document.querySelectorAll('a').length,
        withText: document.querySelectorAll('a:not(:empty)').length
      }
    };

    setTestResults(prev => ({ ...prev, structure: { ...prev.structure, ...results } }));

    // Log issues
    const newIssues = [];
    if (results.headings.h1 === 0) {
      newIssues.push({ severity: 'error', message: 'No H1 heading found on page' });
    } else if (results.headings.h1 > 1) {
      newIssues.push({ severity: 'warning', message: 'Multiple H1 headings found on page' });
    }

    if (results.imgAlt.withAlt < results.imgAlt.total) {
      newIssues.push({ 
        severity: 'error', 
        message: `${results.imgAlt.total - results.imgAlt.withAlt} images missing alt text` 
      });
    }

    if (results.links.withText < results.links.total) {
      newIssues.push({ 
        severity: 'error', 
        message: `${results.links.total - results.links.withText} links missing descriptive text` 
      });
    }

    return newIssues;
  };

  const checkPerformance = async () => {
    const results = {
      loadTime: window.performance.timing.loadEventEnd - window.performance.timing.navigationStart,
      mobileResponsive: window.matchMedia('(max-width: 768px)').matches,
      https: window.location.protocol === 'https:'
    };

    setTestResults(prev => ({ ...prev, performance: results }));

    // Log issues
    const newIssues = [];
    if (results.loadTime > 3000) {
      newIssues.push({ 
        severity: 'error', 
        message: `Page load time is ${(results.loadTime / 1000).toFixed(2)}s (should be under 3s)` 
      });
    } else if (results.loadTime > 2000) {
      newIssues.push({ 
        severity: 'warning', 
        message: `Page load time is ${(results.loadTime / 1000).toFixed(2)}s (could be improved)` 
      });
    }

    if (!results.mobileResponsive) {
      newIssues.push({ severity: 'error', message: 'Page is not mobile responsive' });
    }

    if (!results.https) {
      newIssues.push({ severity: 'error', message: 'Site is not served over HTTPS' });
    }

    return newIssues;
  };

  const checkSitemap = async () => {
    try {
      const response = await fetch('/sitemap.xml');
      const hasSitemap = response.ok;
      
      setTestResults(prev => ({ 
        ...prev, 
        structure: { ...prev.structure, sitemap: hasSitemap } 
      }));

      if (!hasSitemap) {
        return [{ severity: 'warning', message: 'Sitemap.xml not found' }];
      }
    } catch (error) {
      setTestResults(prev => ({ 
        ...prev, 
        structure: { ...prev.structure, sitemap: false } 
      }));
      return [{ severity: 'error', message: 'Error checking sitemap.xml' }];
    }
    return [];
  };

  const checkRobotsTxt = async () => {
    try {
      const response = await fetch('/robots.txt');
      const hasRobotsTxt = response.ok;
      
      setTestResults(prev => ({ 
        ...prev, 
        structure: { ...prev.structure, robotsTxt: hasRobotsTxt } 
      }));

      if (!hasRobotsTxt) {
        return [{ severity: 'warning', message: 'Robots.txt not found' }];
      }
    } catch (error) {
      setTestResults(prev => ({ 
        ...prev, 
        structure: { ...prev.structure, robotsTxt: false } 
      }));
      return [{ severity: 'error', message: 'Error checking robots.txt' }];
    }
    return [];
  };

  const runAllTests = async () => {
    setIssues([]);
    
    const metaIssues = checkMetaTags();
    const structureIssues = checkStructure();
    const performanceIssues = await checkPerformance();
    const sitemapIssues = await checkSitemap();
    const robotsIssues = await checkRobotsTxt();

    setIssues([
      ...metaIssues,
      ...structureIssues,
      ...performanceIssues,
      ...sitemapIssues,
      ...robotsIssues
    ]);
  };

  const getStatusFromValue = (category, metric, value) => {
    if (value === null) return 'error';
    
    switch (category) {
      case 'meta':
        if (metric === 'title' || metric === 'description') {
          return value ? 'good' : 'error';
        }
        return value ? 'good' : 'warning';
      
      case 'structure':
        if (metric === 'headings') {
          return value.h1 === 1 ? 'good' : 'error';
        } else if (metric === 'imgAlt') {
          return value.withAlt === value.total ? 'good' : 'error';
        }
        return value ? 'good' : 'warning';
      
      case 'performance':
        if (metric === 'loadTime') {
          return value < 2000 ? 'good' : value < 3000 ? 'warning' : 'error';
        }
        return value ? 'good' : 'error';
      
      default:
        return 'warning';
    }
  };

  return (
    <SEOContainer>
      <TestSection>
        <h3>SEO and Metadata Tests</h3>
        <TestButton onClick={runAllTests}>
          Run All SEO Tests
        </TestButton>

        <TestResults>
          <h3>Meta Tags</h3>
          <TestGrid>
            <TestCard status={getStatusFromValue('meta', 'title', testResults.meta.title)}>
              <h4>Page Title</h4>
              <p>{testResults.meta.title || 'Not found'}</p>
            </TestCard>
            <TestCard status={getStatusFromValue('meta', 'description', testResults.meta.description)}>
              <h4>Meta Description</h4>
              <p>{testResults.meta.description || 'Not found'}</p>
            </TestCard>
            <TestCard status={getStatusFromValue('meta', 'keywords', testResults.meta.keywords)}>
              <h4>Meta Keywords</h4>
              <p>{testResults.meta.keywords || 'Not found'}</p>
            </TestCard>
            <TestCard status={getStatusFromValue('meta', 'canonical', testResults.meta.canonical)}>
              <h4>Canonical URL</h4>
              <p>{testResults.meta.canonical || 'Not found'}</p>
            </TestCard>
          </TestGrid>

          <h3>Page Structure</h3>
          <TestGrid>
            <TestCard status={getStatusFromValue('structure', 'headings', testResults.structure.headings)}>
              <h4>Headings</h4>
              <p>
                {testResults.structure.headings ? 
                  `H1: ${testResults.structure.headings.h1}, Total: ${testResults.structure.headings.total}` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue('structure', 'imgAlt', testResults.structure.imgAlt)}>
              <h4>Image Alt Text</h4>
              <p>
                {testResults.structure.imgAlt ? 
                  `${testResults.structure.imgAlt.withAlt}/${testResults.structure.imgAlt.total} images with alt` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue('structure', 'sitemap', testResults.structure.sitemap)}>
              <h4>Sitemap.xml</h4>
              <p>{testResults.structure.sitemap ? 'Found' : 'Not found'}</p>
            </TestCard>
            <TestCard status={getStatusFromValue('structure', 'robotsTxt', testResults.structure.robotsTxt)}>
              <h4>Robots.txt</h4>
              <p>{testResults.structure.robotsTxt ? 'Found' : 'Not found'}</p>
            </TestCard>
          </TestGrid>

          <h3>Performance</h3>
          <TestGrid>
            <TestCard status={getStatusFromValue('performance', 'loadTime', testResults.performance.loadTime)}>
              <h4>Page Load Time</h4>
              <p>
                {testResults.performance.loadTime ? 
                  `${(testResults.performance.loadTime / 1000).toFixed(2)}s` : 
                  'Not tested'}
              </p>
            </TestCard>
            <TestCard status={getStatusFromValue('performance', 'mobileResponsive', testResults.performance.mobileResponsive)}>
              <h4>Mobile Responsive</h4>
              <p>{testResults.performance.mobileResponsive ? 'Yes' : 'No'}</p>
            </TestCard>
            <TestCard status={getStatusFromValue('performance', 'https', testResults.performance.https)}>
              <h4>HTTPS</h4>
              <p>{testResults.performance.https ? 'Enabled' : 'Disabled'}</p>
            </TestCard>
          </TestGrid>

          {issues.length > 0 && (
            <>
              <h3>Issues Found</h3>
              <IssueList>
                {issues.map((issue, index) => (
                  <IssueItem key={index} severity={issue.severity}>
                    {issue.message}
                  </IssueItem>
                ))}
              </IssueList>
            </>
          )}
        </TestResults>
      </TestSection>
    </SEOContainer>
  );
};

export default SEOTest; 